/**
 * RecordsBrowser
 *
 * List-detail shell for the Records page, mounted by EntitiesPage. The URL is
 * the source of truth for filters: entity (path), and group/status/q (query
 * params), so views are shareable and back-button friendly. Row clicks navigate
 * (preserving filters) so the scope-driven Entity detail updates.
 *
 * At md and wider the panes sit side by side, split by a `PanelResizeHandle`
 * over `uiState.recordListPane` (clamped, keyboard-adjustable, remembered per
 * user; its highlight is CSS, so pointing at it re-renders nothing). Below md
 * (`md === false`; antd's first-render `{}` counts as wide) the route drives a
 * master-detail stack: `/entities/:entityId` shows only the list at full width,
 * `/records/:recordId` shows only the detail with a back control that keeps the
 * search params. A stack has no splitter. The pane slots keep their JSX order,
 * so crossing md never remounts the surviving pane.
 */
import { FC, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Grid, theme } from 'antd';
import { useSnapshot } from 'valtio';

import { RecordStatus } from '#types/System.Parameters.types';
import { clampPanelWidth, uiState } from '../../store/uiState';
import PanelResizeHandle from '../atoms/PanelResizeHandle';
import FlexPage from '../atoms/FlexPage';
import FlexBox from '../atoms/FlexBox';
import Navb from '../Navb';
import RecordListPanel from './RecordListPanel';
import RecordDetailPanel from './RecordDetailPanel';

const RecordsBrowser: FC = () => {
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const { entityId, recordId } = useParams<{ entityId?: string; recordId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { md } = Grid.useBreakpoint();
    const stacked = md === false;
    const showList = !stacked || !recordId;
    const showDetail = !stacked || !!recordId;

    const { width: storedListWidth } = useSnapshot(uiState.recordListPane, { sync: true });
    const listWidth = clampPanelWidth('recordListPane', storedListWidth);

    const query = searchParams.get('q') ?? '';
    const statusParam = searchParams.get('status');
    const validStatuses = Object.values(RecordStatus) as string[];
    const statusFilters = (statusParam ?? '')
        .split(',')
        .filter((s): s is RecordStatus => validStatuses.includes(s));
    const groupIds = (searchParams.get('group') ?? '').split(',').filter(Boolean);

    const updateParam = useCallback(
        (key: string, value: string | null) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (value) next.set(key, value);
                    else next.delete(key);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const setQuery = useCallback((value: string) => updateParam('q', value || null), [updateParam]);
    const setStatusFilters = useCallback(
        (statuses: RecordStatus[]) => updateParam('status', statuses.length ? statuses.join(',') : null),
        [updateParam],
    );
    const setGroupIds = useCallback((ids: string[]) => updateParam('group', ids.length ? ids.join(',') : null), [updateParam]);

    const openRecord = useCallback(
        (rid: string) => {
            navigate({ pathname: `/entities/${entityId}/records/${rid}`, search: searchParams.toString() });
        },
        [navigate, entityId, searchParams],
    );

    const backToList = useCallback(
        () => navigate({ pathname: `/entities/${entityId}`, search: searchParams.toString() }),
        [navigate, entityId, searchParams],
    );

    const selectEntity = useCallback(
        (nextEntityId: string) => {
            const next = new URLSearchParams();
            if (statusParam) next.set('status', statusParam);
            navigate({ pathname: `/entities/${nextEntityId}`, search: next.toString() });
        },
        [navigate, statusParam],
    );

    return (
        <FlexPage style={{ height: '100dvh', minHeight: 0, overflow: 'hidden' }}>
            <Navb />
            <FlexBox gap={0} style={{ flex: 1, minHeight: 0, width: '100%', alignItems: 'stretch' }}>
                {showList && (
                    <RecordListPanel
                        key={entityId ?? 'none'}
                        width={stacked ? '100%' : listWidth}
                        entityId={entityId ?? ''}
                        recordId={recordId}
                        initialQuery={query}
                        onQueryChange={setQuery}
                        statusFilters={statusFilters}
                        setStatusFilters={setStatusFilters}
                        groupIds={groupIds}
                        setGroupIds={setGroupIds}
                        onOpenRecord={openRecord}
                        onSelectEntity={selectEntity}
                    />
                )}
                {!stacked && (
                    <PanelResizeHandle
                        panel="recordListPane"
                        anchor="left"
                        label="Resize record list"
                        style={{
                            alignSelf: 'stretch',
                            width: 6,
                            borderInlineStart: `1px solid ${token.colorBorderSecondary}`,
                            '--panel-grip-color': token.colorTextQuaternary,
                            '--panel-grip-color-active': token.colorPrimary,
                            '--panel-grip-width': `${token.paddingXXS}px`,
                            '--panel-grip-height': `${token.controlHeightLG}px`,
                            '--panel-grip-radius': `${token.borderRadiusSM}px`,
                            '--panel-track-active': token.colorPrimaryBg,
                        }}
                    />
                )}
                {showDetail && (
                    <RecordDetailPanel
                        entityId={entityId}
                        recordId={recordId}
                        onBack={stacked ? backToList : undefined}
                    />
                )}
            </FlexBox>
        </FlexPage>
    );
};

export default RecordsBrowser;
